// SEO utilities and structured data generators

export const BASE_URL = 'https://nearshore.africa';

export interface PageSEOConfig {
  path: string;
  titleEn: string;
  titleDe: string;
  descriptionEn: string;
  descriptionDe: string;
  keywords?: string;
  type?: 'website' | 'article';
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  image?: string;
}

// Organization JSON-LD schema
export const getOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Nearshore.Africa',
  alternateName: 'Nearshore Africa',
  url: BASE_URL,
  logo: `${BASE_URL}/favicon.png`,
  description: 'Your strategic partner for software development outsourcing in Africa. Access skilled developers, save up to 60% on costs with nearshore outsourcing.',
  foundingDate: '2015',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Motorstrasse 4',
    addressLocality: 'Stuttgart',
    postalCode: '70499',
    addressCountry: 'DE'
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+49-711-219-559-0',
    contactType: 'sales',
    email: 'nearshore.africa@evolvice.de',
    availableLanguage: ['English', 'German']
  },
  knowsAbout: [
    'Nearshore Software Development',
    'Staff Augmentation',
    'Dedicated Development Teams',
    'React', 'TypeScript', 'Node.js', 'Python', 'Java', '.NET',
    'Flutter', 'React Native',
    'AWS', 'Azure', 'GCP', 'DevOps'
  ],
  sameAs: [
    'https://www.evolvice.de',
    'https://www.linkedin.com/company/evolvice-gmbh'
  ],
  parentOrganization: {
    '@type': 'Organization',
    name: 'Evolvice GmbH',
    url: 'https://www.evolvice.de'
  }
});

// Website JSON-LD schema
export const getWebsiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Nearshore.Africa',
  alternateName: 'Nearshore Africa',
  url: BASE_URL,
  description: 'Your strategic partner for software development outsourcing in Africa',
  inLanguage: ['en', 'de'],
  publisher: {
    '@type': 'Organization',
    name: 'Nearshore.Africa'
  }
});

// BreadcrumbList JSON-LD schema
export const getBreadcrumbSchema = (items: { name: string; url: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url
  }))
});

// FAQPage JSON-LD schema
export const getFAQSchema = (faqs: { question: string; answer: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(faq => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer
    }
  }))
});

// Article JSON-LD schema for blog posts (E-E-A-T enhanced)
export const getArticleSchema = (article: {
  title: string;
  description: string;
  url: string;
  image: string;
  datePublished: string;
  dateModified?: string | undefined;
  author?: string | undefined;
  authorUrl?: string | undefined;
  authorJobTitle?: string | undefined;
  reviewer?: string | undefined;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: article.title,
  description: article.description,
  image: article.image,
  url: article.url,
  datePublished: article.datePublished,
  dateModified: article.dateModified || article.datePublished,
  author: {
    '@type': 'Person',
    name: article.author || 'Evolvice Editorial Team',
    jobTitle: article.authorJobTitle || 'Editorial Team, Nearshore.Africa',
    url: article.authorUrl || 'https://www.linkedin.com/company/evolvice-gmbh',
    worksFor: {
      '@type': 'Organization',
      name: 'Evolvice GmbH',
      url: 'https://www.evolvice.de'
    }
  },
  ...(article.reviewer
    ? {
        reviewedBy: {
          '@type': 'Person',
          name: article.reviewer,
          worksFor: { '@type': 'Organization', name: 'Evolvice GmbH' }
        }
      }
    : {}),
  publisher: {
    '@type': 'Organization',
    name: 'Nearshore.Africa',
    logo: {
      '@type': 'ImageObject',
      url: `${BASE_URL}/favicon.png`
    }
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': article.url
  }
});

// Service JSON-LD schema
export const getServiceSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Nearshore Software Development Outsourcing',
  description: 'Professional software development outsourcing services connecting European businesses with skilled African tech talent.',
  provider: {
    '@type': 'Organization',
    name: 'Nearshore.Africa'
  },
  areaServed: ['Europe', 'Middle East', 'Germany', 'Austria', 'Switzerland'],
  serviceType: 'Software Development Outsourcing',
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Nearshore Development Services',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Dedicated Development Teams',
          description: 'Full-time developers integrated into your workflows'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Staff Augmentation',
          description: 'Scale your existing team with specialized talent'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Project-Based Outsourcing',
          description: 'End-to-end delivery of defined projects'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Web & Mobile App Development'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Cloud Solutions & DevOps'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'QA & Testing'
        }
      }
    ]
  }
});

// Get base path without language prefix
export const getBasePathFromUrl = (path: string): string => {
  if (path.startsWith('/en/')) {
    return path.slice(3);
  }
  if (path === '/en') {
    return '/';
  }
  return path;
};

// Generate hreflang links for a given path (expects base path without /en prefix)
export const getHreflangLinks = (basePath: string) => {
  const cleanPath = basePath === '/' ? '' : basePath;
  return [
    { rel: 'alternate', hrefLang: 'de', href: `${BASE_URL}${cleanPath}` },
    { rel: 'alternate', hrefLang: 'en', href: `${BASE_URL}/en${cleanPath}` },
    { rel: 'alternate', hrefLang: 'x-default', href: `${BASE_URL}${cleanPath}` }
  ];
};

// Generate canonical URL based on current language
export const getCanonicalUrl = (basePath: string, language: string = 'de') => {
  const cleanPath = basePath === '/' ? '' : basePath;
  if (language === 'en') {
    return `${BASE_URL}/en${cleanPath}`;
  }
  return `${BASE_URL}${cleanPath}`;
};

// Page-specific SEO configurations
export const PAGE_SEO_CONFIG = {
  home: {
    path: '/',
    titleEn: 'Nearshore Software Development Company in Africa | Nearshore.Africa',
    titleDe: 'Nearshore-Softwareentwicklungsunternehmen in Afrika | IT-Outsourcing für Deutschland',
    descriptionEn: 'Leading nearshore software development company in Africa. Save up to 60% vs. Germany & Eastern Europe with vetted Egyptian, Kenyan & Moroccan developers — same timezone, GDPR-ready.',
    descriptionDe: 'Führendes Nearshore-Softwareentwicklungsunternehmen in Afrika für deutsche Unternehmen. Bis zu 60 % günstiger als Osteuropa, gleiche Zeitzone (UTC+1/+2), DSGVO-konform, geprüfte Entwickler aus Ägypten, Kenia & Marokko.',
    keywords: 'nearshore software development company in africa, nearshore softwareentwicklung afrika, IT-Outsourcing Deutschland, software outsourcing africa, nearshoring deutschland, IT-Dienstleister Afrika, Entwickler Ägypten, nearshore vs osteuropa'
  },
  whatIsNearshoring: {
    path: '/what-is-nearshoring',
    titleEn: 'Why Nearshore Software Development from Africa? | Benefits & Costs',
    titleDe: 'Warum Nearshore-Softwareentwicklung aus Afrika? | Vorteile, Kosten & Vergleich',
    descriptionEn: 'Why nearshore software development from Africa: 60% cost savings vs. Germany, EU timezone overlap, English-speaking developers, GDPR compliance and a comparison vs. Eastern Europe.',
    descriptionDe: 'Warum Nearshore-Softwareentwicklung aus Afrika? Bis zu 60 % Kostenersparnis gegenüber Deutschland, gleiche Zeitzone, DSGVO-konform und Vergleich Afrika vs. Osteuropa für IT-Outsourcing.',
    keywords: 'why nearshore software development from africa, warum nearshore afrika, nearshore softwareentwicklung afrika, IT-Outsourcing Afrika vs Osteuropa, nearshoring kosten, nearshore vorteile, softwareentwicklung outsourcing deutschland'
  },
  nearshoringVsOffshoring: {
    path: '/nearshoring-vs-offshoring-vs-outsourcing',
    titleEn: 'Nearshoring vs Offshoring vs Outsourcing | Compare',
    titleDe: 'Nearshoring vs Offshoring vs Outsourcing | Vergleich',
    descriptionEn: 'Compare nearshoring, offshoring and outsourcing models. Find the best approach for your business.',
    descriptionDe: 'Nearshoring, Offshoring und Outsourcing vergleichen. Den besten Ansatz für Ihr Unternehmen finden.',
    keywords: 'nearshoring vs offshoring, outsourcing comparison, best outsourcing model, nearshore benefits'
  },
  companies: {
    path: '/companies',
    titleEn: 'Top Software Companies in Africa | Nearshore.Africa',
    titleDe: 'Top Software-Unternehmen in Afrika | Nearshore.Africa',
    descriptionEn: 'Discover leading software companies in Africa. Compare nearshore partners in Egypt, Kenya, Ghana.',
    descriptionDe: 'Führende Software-Unternehmen in Afrika. Nearshore-Partner in Ägypten, Kenia, Ghana vergleichen.',
    keywords: 'African software companies, nearshore partners Africa, Egypt developers, Kenya tech companies'
  },
  blog: {
    path: '/blog',
    titleEn: 'Blog | Nearshore Outsourcing Insights | Africa',
    titleDe: 'Blog | Nearshore Outsourcing Insights | Afrika',
    descriptionEn: 'Expert insights on nearshore software outsourcing in Africa. Guides, cost analysis and trends.',
    descriptionDe: 'Experten-Einblicke in Nearshore-Outsourcing in Afrika. Leitfäden, Kostenanalysen und Trends.',
    keywords: 'nearshore outsourcing blog, software development Africa, tech insights, outsourcing guides'
  },
  privacyPolicy: {
    path: '/privacy-policy',
    titleEn: 'Privacy Policy | Nearshore.Africa',
    titleDe: 'Datenschutzerklärung | Nearshore.Africa',
    descriptionEn: 'Privacy Policy for Nearshore.Africa. How we collect, use and protect your data (GDPR).',
    descriptionDe: 'Datenschutzerklärung für Nearshore.Africa. Wie wir Ihre Daten erheben, nutzen und schützen.',
    keywords: 'privacy policy, data protection, GDPR compliance'
  },
  imprint: {
    path: '/imprint',
    titleEn: 'Imprint | Nearshore.Africa',
    titleDe: 'Impressum | Nearshore.Africa',
    descriptionEn: 'Legal information for Nearshore.Africa – Evolvice GmbH, Stuttgart, Germany.',
    descriptionDe: 'Rechtliche Informationen für Nearshore.Africa – Evolvice GmbH, Stuttgart.',
    keywords: 'imprint, legal information, Evolvice GmbH, company details'
  }
} satisfies Record<string, PageSEOConfig>;
